import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Surface, Badge, Button, Grid, PageHeader, Input, Select, Modal, Tabs, Alert } from '../../ui/components';
import { RefreshCw, Eye, Search, Edit, User, Home, Briefcase, Wallet } from 'lucide-react';
import FloatingOwenAssistant from '../../components/common/FloatingOwenAssistant';
import FloatingKnowledge from '../../components/common/FloatingKnowledge';

const DetailRow = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--lk-border-subtle)', fontSize: 'var(--lk-text-sm)' }}>
    <span style={{ color: 'var(--lk-text-tertiary)', fontWeight: 500 }}>{label}</span>
    <span style={{ color: 'var(--lk-text-primary)' }}>{value}</span>
  </div>
);

const AdminApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedApp, setSelectedApp] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedApp, setEditedApp] = useState(null);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', variant: 'success' });
  const [detailsTab, setDetailsTab] = useState(0);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const { data, error } = await supabase
        .from('loan_applications')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setApplications(data || []);
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toLocaleString()}`;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'secondary',
      in_progress: 'info',
      submitted_for_underwriting: 'warning',
      underwriting_in_progress: 'warning',
      conditionally_cleared: 'info',
      cleared_for_marketplace: 'success',
      in_marketplace: 'primary',
      bid_accepted: 'success',
      closed: 'success',
      needs_work: 'danger',
    };
    return colors[status] || 'secondary';
  };

  const handleViewDetails = (app) => {
    setSelectedApp(app);
    setEditedApp({ ...app });
    setDetailsOpen(true);
    setEditMode(false);
    setDetailsTab(0);
  };

  const handleEdit = () => {
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditedApp({ ...selectedApp });
    setEditMode(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('loan_applications')
        .update({
          status: editedApp.status,
          loan_amount: editedApp.loan_amount,
          readiness_score: editedApp.readiness_score,
        })
        .eq('id', editedApp.id);

      if (error) throw error;

      setSelectedApp(editedApp);
      setEditMode(false);
      setAlert({ show: true, message: 'Application updated successfully', variant: 'success' });
      fetchApplications();
    } catch (error) {
      console.error('Error updating application:', error);
      setAlert({ show: true, message: 'Failed to update application', variant: 'danger' });
    } finally {
      setSaving(false);
    }
  };

  const filteredApplications = applications.filter((app) => {
    const matchesSearch = 
      app.anonymous_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.borrower_info?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.borrower_info?.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.borrower_info?.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const paginatedApplications = filteredApplications.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const totalPages = Math.ceil(filteredApplications.length / rowsPerPage);

  if (loading) {
    return (
      <div style={{ padding: 'var(--lk-space-8)' }}>
        <div className="lk-spinner lk-spinner-lg" style={{ margin: '20vh auto' }} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Applications"
        description="Manage all loan applications"
        actions={
          <Button variant="secondary" icon={RefreshCw} onClick={fetchApplications}>
            Refresh
          </Button>
        }
      />

      {/* Alert */}
      {alert.show && (
        <Alert
          variant={alert.variant === 'danger' ? 'error' : alert.variant}
          onClose={() => setAlert({ ...alert, show: false })}
          style={{ marginBottom: 'var(--lk-space-4)' }}
        >
          {alert.message}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid cols={4} gap="var(--lk-space-4)" style={{ marginBottom: 'var(--lk-space-6)' }}>
        <Surface padding="var(--lk-space-5)">
          <div className="lk-overline" style={{ marginBottom: 4 }}>Total Applications</div>
          <div style={{ fontSize: 'var(--lk-text-2xl)', fontWeight: 700 }}>{applications.length}</div>
        </Surface>
        <Surface padding="var(--lk-space-5)">
          <div className="lk-overline" style={{ marginBottom: 4 }}>In Progress</div>
          <div style={{ fontSize: 'var(--lk-text-2xl)', fontWeight: 700, color: 'var(--lk-primary-600)' }}>
            {applications.filter(a => a.status === 'in_progress' || a.status === 'underwriting_in_progress').length}
          </div>
        </Surface>
        <Surface padding="var(--lk-space-5)">
          <div className="lk-overline" style={{ marginBottom: 4 }}>In Marketplace</div>
          <div style={{ fontSize: 'var(--lk-text-2xl)', fontWeight: 700, color: 'var(--lk-primary-500)' }}>
            {applications.filter(a => a.status === 'in_marketplace').length}
          </div>
        </Surface>
        <Surface padding="var(--lk-space-5)">
          <div className="lk-overline" style={{ marginBottom: 4 }}>Closed</div>
          <div style={{ fontSize: 'var(--lk-text-2xl)', fontWeight: 700, color: 'var(--lk-success-600)' }}>
            {applications.filter(a => a.status === 'closed').length}
          </div>
        </Surface>
      </Grid>

      {/* Filters */}
      <Surface padding="var(--lk-space-4)" style={{ marginBottom: 'var(--lk-space-4)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 'var(--lk-space-4)', alignItems: 'end' }}>
          <Input
            placeholder="Search by ID, name, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="in_progress">In Progress</option>
            <option value="submitted_for_underwriting">Submitted for Underwriting</option>
            <option value="underwriting_in_progress">Underwriting in Progress</option>
            <option value="cleared_for_marketplace">Cleared for Marketplace</option>
            <option value="in_marketplace">In Marketplace</option>
            <option value="bid_accepted">Bid Accepted</option>
            <option value="closed">Closed</option>
            <option value="needs_work">Needs Work</option>
          </Select>
          <div style={{ fontSize: 'var(--lk-text-sm)', color: 'var(--lk-text-quaternary)', textAlign: 'right' }}>
            Showing {paginatedApplications.length} of {filteredApplications.length}
          </div>
        </div>
      </Surface>

      {/* Applications Table */}
      <Surface padding="0" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="lk-table">
            <thead>
              <tr>
                <th>Application ID</th>
                <th>Borrower</th>
                <th>Product</th>
                <th style={{ textAlign: 'right' }}>Loan Amount</th>
                <th style={{ textAlign: 'center' }}>Credit Score</th>
                <th style={{ textAlign: 'center' }}>Readiness</th>
                <th>Status</th>
                <th>Created</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedApplications.length > 0 ? (
                paginatedApplications.map((app) => (
                  <tr key={app.id}>
                    <td style={{ fontFamily: 'monospace', fontWeight: 500 }}>
                      {app.anonymous_id || `LK-${app.id.slice(0, 8).toUpperCase()}`}
                      {app.borrower_info?.is_test && (
                        <Badge variant="warning" style={{ marginLeft: 4, fontSize: '0.6rem' }}>T</Badge>
                      )}
                    </td>
                    <td>
                      <div style={{ fontWeight: 500 }}>
                        {app.borrower_info?.first_name || '-'} {app.borrower_info?.last_name || ''}
                      </div>
                      <div style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-quaternary)' }}>{app.borrower_info?.email || '-'}</div>
                    </td>
                    <td>
                      <Badge variant="neutral">
                        {app.product_type?.replace(/_/g, ' ') || '-'}
                      </Badge>
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(app.loan_amount)}</td>
                    <td style={{ textAlign: 'center', fontWeight: 600 }}>{app.credit_score || '-'}</td>
                    <td style={{ textAlign: 'center' }}>
                      <Badge variant={app.readiness_score >= 80 ? 'success' : app.readiness_score >= 60 ? 'warning' : 'danger'}>
                        {app.readiness_score || 0}
                      </Badge>
                    </td>
                    <td>
                      <Badge variant={getStatusColor(app.status)}>
                        {app.status?.replace(/_/g, ' ')}
                      </Badge>
                    </td>
                    <td style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-tertiary)' }}>{formatDate(app.created_at)}</td>
                    <td style={{ textAlign: 'center' }}>
                      <Button size="sm" variant="ghost" icon={Eye} onClick={() => handleViewDetails(app)} />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', color: 'var(--lk-text-quaternary)', padding: 'var(--lk-space-8)' }}>
                    No applications found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filteredApplications.length > rowsPerPage && (
          <div style={{ padding: 'var(--lk-space-4) var(--lk-space-6)', borderTop: '1px solid var(--lk-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 'var(--lk-text-sm)', color: 'var(--lk-text-quaternary)' }}>
              Page {page + 1} of {totalPages}
            </div>
            <div style={{ display: 'flex', gap: 'var(--lk-space-2)' }}>
              <Button size="sm" variant="secondary" disabled={page === 0} onClick={() => setPage(page - 1)}>
                Previous
              </Button>
              <Button size="sm" variant="secondary" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>
                Next
              </Button>
            </div>
            <select
              className="lk-select"
              style={{ width: 'auto', padding: '4px 8px', fontSize: 'var(--lk-text-sm)' }}
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(0);
              }}
            >
              <option value={10}>10 per page</option>
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
          </div>
        )}
      </Surface>

      {/* Details Modal */}
      <Modal
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        title={
          <span>
            Application Details
            {selectedApp && (
              <Badge variant={getStatusColor(selectedApp.status)} style={{ marginLeft: 8 }}>
                {selectedApp.status?.replace(/_/g, ' ')}
              </Badge>
            )}
          </span>
        }
        footer={
          editMode ? (
            <>
              <Button variant="secondary" onClick={handleCancelEdit}>Cancel</Button>
              <Button variant="primary" onClick={handleSave} loading={saving}>Save Changes</Button>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={() => setDetailsOpen(false)}>Close</Button>
              <Button variant="primary" icon={Edit} onClick={handleEdit}>Edit</Button>
            </>
          )
        }
      >
        {selectedApp && (
          <>
            <Tabs
              tabs={[
                { id: 0, label: 'Overview' },
                { id: 1, label: 'Borrower Info' },
                { id: 2, label: 'Property' },
                { id: 3, label: 'Financials' },
              ]}
              activeTab={detailsTab}
              onChange={setDetailsTab}
            />

            <div style={{ marginTop: 'var(--lk-space-4)' }}>
              {detailsTab === 0 && (
                <Grid cols={2} gap="var(--lk-space-4)">
                  <Surface padding="var(--lk-space-5)">
                    <div style={{ fontWeight: 600, marginBottom: 'var(--lk-space-3)' }}>Application Info</div>
                    <DetailRow label="ID" value={selectedApp.anonymous_id || selectedApp.id} />
                    <DetailRow label="Created" value={formatDate(selectedApp.created_at)} />
                    <DetailRow label="Updated" value={formatDate(selectedApp.updated_at)} />
                    <DetailRow label="Product" value={selectedApp.product_type?.replace(/_/g, ' ')} />
                  </Surface>
                  <Surface padding="var(--lk-space-5)">
                    <div style={{ fontWeight: 600, marginBottom: 'var(--lk-space-3)' }}>Loan Details</div>
                    {editMode ? (
                      <>
                        <Input
                          label="Loan Amount"
                          type="number"
                          value={editedApp.loan_amount}
                          onChange={(e) => setEditedApp({ ...editedApp, loan_amount: Number(e.target.value) })}
                        />
                        <Input
                          label="Readiness Score"
                          type="number"
                          value={editedApp.readiness_score}
                          onChange={(e) => setEditedApp({ ...editedApp, readiness_score: Number(e.target.value) })}
                          style={{ marginTop: 'var(--lk-space-2)' }}
                        />
                        <Select
                          label="Status"
                          value={editedApp.status}
                          onChange={(e) => setEditedApp({ ...editedApp, status: e.target.value })}
                          style={{ marginTop: 'var(--lk-space-2)' }}
                        >
                          <option value="draft">Draft</option>
                          <option value="in_progress">In Progress</option>
                          <option value="submitted_for_underwriting">Submitted for Underwriting</option>
                          <option value="underwriting_in_progress">Underwriting in Progress</option>
                          <option value="cleared_for_marketplace">Cleared for Marketplace</option>
                          <option value="in_marketplace">In Marketplace</option>
                          <option value="bid_accepted">Bid Accepted</option>
                          <option value="closed">Closed</option>
                          <option value="needs_work">Needs Work</option>
                        </Select>
                      </>
                    ) : (
                      <>
                        <DetailRow label="Loan Amount" value={formatCurrency(selectedApp.loan_amount)} />
                        <DetailRow label="Credit Score" value={selectedApp.credit_score || '-'} />
                        <DetailRow label="DTI Ratio" value={selectedApp.dti_ratio ? `${selectedApp.dti_ratio}%` : '-'} />
                        <DetailRow label="Readiness Score" value={
                          <Badge variant={selectedApp.readiness_score >= 80 ? 'success' : selectedApp.readiness_score >= 60 ? 'warning' : 'danger'}>
                            {selectedApp.readiness_score || 0}
                          </Badge>
                        } />
                      </>
                    )}
                  </Surface>
                </Grid>
              )}

              {detailsTab === 1 && (
                <Surface padding="var(--lk-space-5)">
                  <div style={{ fontWeight: 600, marginBottom: 'var(--lk-space-3)', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <User size={16} /> Borrower Information
                  </div>
                  <Grid cols={2} gap="var(--lk-space-4)">
                    <div>
                      <DetailRow label="Name" value={`${selectedApp.borrower_info?.first_name || ''} ${selectedApp.borrower_info?.last_name || ''}`} />
                      <DetailRow label="Email" value={selectedApp.borrower_info?.email || '-'} />
                      <DetailRow label="Phone" value={selectedApp.borrower_info?.phone || '-'} />
                    </div>
                    <div>
                      <DetailRow label="Date of Birth" value={selectedApp.borrower_info?.dob || '-'} />
                      <DetailRow label="SSN" value={selectedApp.borrower_info?.ssn ? '***-**-' + selectedApp.borrower_info.ssn.slice(-4) : '-'} />
                    </div>
                  </Grid>
                </Surface>
              )}

              {detailsTab === 2 && (
                <Surface padding="var(--lk-space-5)">
                  <div style={{ fontWeight: 600, marginBottom: 'var(--lk-space-3)', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Home size={16} /> Property Information
                  </div>
                  <DetailRow label="Address" value={selectedApp.property_info?.address || '-'} />
                  <DetailRow label="Property Type" value={selectedApp.property_info?.type || '-'} />
                  <DetailRow label="Property Use" value={selectedApp.property_info?.use || '-'} />
                  <DetailRow label="Purchase Price" value={formatCurrency(selectedApp.property_info?.purchase_price)} />
                  <DetailRow label="Down Payment" value={formatCurrency(selectedApp.property_info?.down_payment)} />
                </Surface>
              )}

              {detailsTab === 3 && (
                <Grid cols={2} gap="var(--lk-space-4)">
                  <Surface padding="var(--lk-space-5)">
                    <div style={{ fontWeight: 600, marginBottom: 'var(--lk-space-3)', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Briefcase size={16} /> Income
                    </div>
                    <DetailRow label="Monthly Income" value={formatCurrency(selectedApp.income_info?.monthly)} />
                    <DetailRow label="Annual Income" value={formatCurrency(selectedApp.income_info?.annual)} />
                    <DetailRow label="Employment" value={selectedApp.income_info?.employment_type || '-'} />
                  </Surface>
                  <Surface padding="var(--lk-space-5)">
                    <div style={{ fontWeight: 600, marginBottom: 'var(--lk-space-3)', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Wallet size={16} /> Assets
                    </div>
                    <DetailRow label="Total Assets" value={formatCurrency(selectedApp.assets_info?.total)} />
                    <DetailRow label="Liquid Assets" value={formatCurrency(selectedApp.assets_info?.liquid)} />
                  </Surface>
                </Grid>
              )}
            </div>
          </>
        )}
      </Modal>

      {/* Floating AI Assistant & Knowledge */}
      <FloatingOwenAssistant 
        context="admin-applications"
        contextData={{ applications: filteredApplications, filters: { statusFilter, searchTerm } }}
      />
      <FloatingKnowledge 
        customContent={{
          label: 'Applications',
          title: 'Application management',
          body: 'Monitor and manage all loan applications in the system.',
          bullets: [
            'Track application status and progress',
            'Review borrower information and documents',
            'Assign applications to underwriters',
            'Monitor application pipeline health',
          ],
          resources: [
            { label: 'Application workflow', href: '#' },
            { label: 'Status definitions', href: '#' },
          ],
        }}
      />
    </div>
  );
};

export default AdminApplications;
